import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import {
  IcoArchive,
  IcoChevronRight,
  IcoFileText,
  IcoPlus,
  IcoSpinner,
  IcoStar,
  IcoUsers,
} from "../../../components/icons";
import { BrandedLayout } from "../../../components/ui";
import { usePageTitle } from "../../../hooks";
import { rpc } from "../../../rpc";
import { showError } from "../../../stores/errors";
import type { MimicState } from "../../../stores/mimic";
import { isAdmin, type OrganizationListItem, type User } from "../../../types";
import { getErrorMessage } from "../../../utils/errors";

type AdminFormItem = {
  id: string;
  title: string;
  questionCount: number;
  assigneeCount: number;
  archivedAt?: Date | null;
  createdAt: Date;
};

type FormsPageState = {
  currentUser: User;
  mimic: MimicState;
  org: OrganizationListItem;
  adminForms: AdminFormItem[];
};

export async function load(args: LoaderArgs): Promise<FormsPageState> {
  const orgId = args.params.orgId ?? "";

  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const org = orgsResult.organizations.find((o) => o.id === orgId);
  if (!org) {
    throw new RedirectError("/");
  }

  const isOrgAdmin = isAdmin(org.role);

  // Non-admins don't have access to the forms management page
  if (!isOrgAdmin) {
    throw new RedirectError(`/orgs/${orgId}`);
  }

  const formsResult = await rpc.forms.list({ orgId });

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    org,
    adminForms: formsResult.forms,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Forms");
  const { currentUser, mimic, org, adminForms } = state.value;

  const isCreating = useSignal(false);

  const activeForms = adminForms.filter((f) => !f.archivedAt);
  const archivedForms = adminForms.filter((f) => f.archivedAt);

  async function handleCreate() {
    if (isCreating.value) return;
    isCreating.value = true;

    try {
      const result = await rpc.forms.create({ orgId: org.id });
      navigateTo(`/orgs/${org.id}/forms/${result.form.id}/edit`);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isCreating.value = false;
    }
  }

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={org.id}
      orgName={org.name}
      brandColor={org.brandColor}
      activeTab="forms"
      isAdmin={true}
      formCount={org.formCount}
      hasReports={org.hasReports}
    >
      {/* Page header */}
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold text-text">Forms</h1>
        <div class="flex items-center gap-2">
          <a
            href={`/orgs/${org.id}/forms/bank`}
            class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
          >
            <IcoStar class="size-4" />
            Question Bank
          </a>
          <a
            href={`/orgs/${org.id}/groups`}
            class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
          >
            <IcoUsers class="size-4" />
            Groups
          </a>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating.value}
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
          >
            {isCreating.value ? (
              <IcoSpinner class="size-4 animate-spin" />
            ) : (
              <IcoPlus class="size-4" />
            )}
            New Form
          </button>
        </div>
      </div>

      {/* Forms list */}
      {activeForms.length === 0 && archivedForms.length === 0 ? (
        <div class="rounded-xl border border-border bg-surface p-12 text-center">
          <div class="size-14 mx-auto mb-4 rounded-full bg-surface-muted flex items-center justify-center">
            <IcoFileText class="size-7 text-text-muted" />
          </div>
          <h3 class="font-medium text-text mb-2">No forms yet</h3>
          <p class="text-text-muted mb-6 max-w-sm mx-auto">
            Create your first form to start collecting check-ins from your team.
          </p>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating.value}
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
          >
            {isCreating.value ? (
              <IcoSpinner class="size-4 animate-spin" />
            ) : (
              <IcoPlus class="size-4" />
            )}
            Create your first form
          </button>
        </div>
      ) : (
        <>
          {/* Active forms */}
          {activeForms.length > 0 && (
            <section class="mb-6">
              <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
                Active Forms ({activeForms.length})
              </h2>
              <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
                {activeForms.map((form) => (
                  <FormRow key={form.id} form={form} orgId={org.id} />
                ))}
              </div>
            </section>
          )}

          {/* Archived forms */}
          {archivedForms.length > 0 && (
            <ArchivedFormsSection forms={archivedForms} orgId={org.id} />
          )}
        </>
      )}
    </BrandedLayout>
  );
}

// ============================================================================
// Form Components
// ============================================================================

function ArchivedFormsSection({
  forms,
  orgId,
}: {
  forms: AdminFormItem[];
  orgId: string;
}) {
  return (
    <section>
      <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3 flex items-center gap-2">
        <IcoArchive class="size-4" />
        Archived ({forms.length})
      </h2>
      <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border opacity-70">
        {forms.map((form) => (
          <FormRow key={form.id} form={form} orgId={orgId} isArchived />
        ))}
      </div>
    </section>
  );
}

function FormRow({
  form,
  orgId,
  isArchived,
}: {
  form: AdminFormItem;
  orgId: string;
  isArchived?: boolean;
}) {
  return (
    <a
      href={`/orgs/${orgId}/forms/${form.id}/edit`}
      class="group flex items-center gap-4 px-5 py-4 hover:bg-surface-hover transition-colors"
    >
      <div
        class={`size-10 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ${
          isArchived
            ? "bg-surface-muted text-text-muted ring-border"
            : "bg-[var(--brand-100)] text-[var(--brand-600)] ring-[var(--brand-200)]"
        }`}
      >
        <IcoFileText class="size-5" />
      </div>
      <div class="flex-1 min-w-0">
        <span
          class={`font-medium truncate block ${
            isArchived
              ? "text-text-muted"
              : "text-text group-hover:text-[var(--brand-600)]"
          }`}
        >
          {form.title || "Untitled form"}
        </span>
        <div class="flex items-center gap-3 text-sm text-text-muted">
          <span>
            {form.questionCount}{" "}
            {form.questionCount === 1 ? "question" : "questions"}
          </span>
          {form.assigneeCount > 0 && (
            <span class="inline-flex items-center gap-1">
              <IcoUsers class="size-3.5" />
              {form.assigneeCount}
            </span>
          )}
        </div>
      </div>
      <IcoChevronRight class="size-5 text-text-muted group-hover:text-[var(--brand-500)] transition-colors flex-shrink-0" />
    </a>
  );
}
